import React, { useState, useMemo, useEffect } from 'react';
import { TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowUp, ArrowDown } from 'lucide-react';
import TimezoneBadge from '@/components/ui/timezone-badge';

interface SalesHistoryTableProps {
  data: any[];
  loading?: boolean;
  resetSignal?: number;
  timezoneInfo?: {
    detected_timezone: string;
    timezone_offset: string;
  } | null;
}

const SalesHistoryTable: React.FC<SalesHistoryTableProps> = ({ data, loading, resetSignal = 0, timezoneInfo }) => {
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    setSelectedYear('all');
    setSortDirection('desc');
  }, [resetSignal]);

  // Extract available years from data
  const availableYears = useMemo(() => {
    const years = data.map(row => row.year?.toString()).filter(Boolean);
    return [...new Set(years)].sort((a, b) => parseInt(b) - parseInt(a));
  }, [data]);

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = data.filter(row => row.year?.toString() === selectedYear);
    }

    // Sort by year
    return [...filtered].sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      return sortDirection === 'desc' ? yearB - yearA : yearA - yearB;
    });
  }, [data, selectedYear, sortDirection]);

  // Calculate totals for each month column
  const columnTotals = useMemo(() => {
    if (!filteredAndSortedData.length) return {};

    const totals: Record<string, number> = {};
    const monthColumns = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Total'];

    monthColumns.forEach(month => {
      totals[month] = filteredAndSortedData.reduce((sum, row) => {
        if (month === 'Total') {
          const rawTotal = row.TotalRaw;
          if (rawTotal !== undefined && rawTotal !== null && rawTotal !== '') {
            if (typeof rawTotal === 'number') return sum + rawTotal;
            if (typeof rawTotal === 'string') return sum + (parseFloat(rawTotal) || 0);
          }
        }
        const value = row[month];
        if (typeof value === 'string') {
          // Remove formatting and parse number
          const numValue = parseFloat(value.replace(/[,$]/g, '')) || 0;
          return sum + numValue;
        }
        return sum + (parseFloat(value) || 0);
      }, 0);
    });

    return totals;
  }, [filteredAndSortedData]);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleYearSort = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  if (loading) {
    return (
      <div className="border border-gray-200 rounded-lg p-8 text-center">
        <div className="animate-pulse">Loading sales history...</div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="border border-gray-200 rounded-lg p-8 text-center text-gray-500">
        <p>No sales history data available</p>
      </div>
    );
  }

  const columns = Object.keys(data[0]).filter(column => column !== 'TotalRaw');

  return (
    <div className="space-y-4">
      {/* Filter Controls with Timezone Display */}
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter by Year:</span>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {availableYears.map(year => (
              <SelectItem key={year} value={year}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Timezone Information */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">Data Timezone:</span>
          <TimezoneBadge
            timezone={timezoneInfo?.detected_timezone || 'UTC'}
            offset={timezoneInfo?.timezone_offset || '+00:00'}
            size="sm"
          />
        </div>
      </div>

      {/* Single scroll container so header, body, and totals share horizontal scroll and column alignment */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="relative max-h-[600px] overflow-auto">
          <table className="w-full caption-bottom text-sm">
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column}
                    className="sticky top-0 z-10 bg-white font-semibold border-b-2 border-gray-200 whitespace-nowrap"
                  >
                    {column === 'year' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleYearSort}
                        className="h-auto p-0 font-semibold hover:bg-transparent"
                      >
                        Year
                        {sortDirection === 'desc' ? (
                          <ArrowDown className="ml-1 h-4 w-4" />
                        ) : (
                          <ArrowUp className="ml-1 h-4 w-4" />
                        )}
                      </Button>
                    ) : (
                      column.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedData.map((row, index) => (
                <TableRow key={index} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <TableCell key={column} className="font-medium">
                      {typeof row[column] === 'object' ? JSON.stringify(row[column]) : row[column]?.toString() || ''}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter className="sticky bottom-0 z-10 border-t-2 border-gray-300 bg-gray-50 hover:bg-gray-50 [&>tr]:border-b-0">
              <TableRow className="font-bold bg-gray-100 hover:bg-gray-100">
                <TableCell className="font-bold text-gray-900 bg-gray-100">TOTALS</TableCell>
                {columns.slice(1).map((column) => (
                  <TableCell key={column} className="font-bold text-gray-900 bg-gray-100">
                    {columnTotals[column] !== undefined ? formatCurrency(columnTotals[column]) : ''}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </table>
        </div>
      </div>

      {/* Summary Info */}
      <div className="text-sm text-gray-600">
        Showing {filteredAndSortedData.length} of {data.length} years
        {selectedYear !== 'all' && ` (filtered by ${selectedYear})`}
      </div>
    </div>
  );
};

export default SalesHistoryTable;
